import React, { useState, useEffect, useRef } from 'react';
// Use global ProjectInterface from vite-env.d.ts which includes owner_id
import { useProjectUsers } from '../../hooks/useProjectUsers';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useUsersStore, useUsersPage } from '../../state/users';
import { AddUserModal } from './AddUserModal';
import { ChangeRoleModal } from './ChangeRoleModal';
import { ChangeEscalationLevelModal } from './ChangeEscalationLevelModal';
import { EditUserModal } from './EditUserModal';
import { ProjectTeamErrorBoundary } from './ProjectTeamErrorBoundary';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { Avatar } from '../Avatar';
import { useEscalationMatrixStore, useEscalationMatrixPage } from '../../state/escalationMatrix';
import { useKeycloak } from '@react-keycloak/web';
import { resendInvitation, getUserProfile } from '../../api/users';
import { formatPhoneForDisplay } from '../../utils/phoneValidation';
import { getDisplayRoleName } from '../../helpers/projectPermissions';
import { useCanManageTeam, useProjectAccessContext } from '../../context/projectAccess';
import { LoadingSpinner } from '../LoadingSpinner';

interface ProjectTeamProps {
  projectData: ProjectInterface | 'not-found';
  externalShowAddModal?: boolean;
  externalSetShowAddModal?: (show: boolean) => void;
  onProjectUpdate?: () => Promise<void>;
  archived?: boolean;
  projectUsers?: ProjectUsersResponse;
}

export const ProjectTeam: React.FC<ProjectTeamProps> = ({
  projectData,
  externalShowAddModal,
  externalSetShowAddModal,
  onProjectUpdate,
  archived = false,
  projectUsers: archivedProjectUsers,
}) => {
  const { access: projectAccess, error: projectAccessError, reloadAccess } = useProjectAccessContext();
  const canManage = useCanManageTeam();
  // Add cleanup flag to prevent DOM operations after unmount. Must be a ref,
  // not state: under React StrictMode's dev-only mount/cleanup/remount
  // simulation, a state-based flag set `true` by the cleanup never gets reset
  // back to `false`, permanently disabling every effect gated on it.
  const isUnmountedRef = useRef(false);
  const [showAddModal, setShowAddModal] = useState(false);
  
  // Use external state if provided, otherwise use internal state
  const isShowAddModal = externalShowAddModal !== undefined ? externalShowAddModal : showAddModal;
  const setIsShowAddModal = externalSetShowAddModal || setShowAddModal;
  const [showChangeRoleModal, setShowChangeRoleModal] = useState(false);
  const [showChangeEscalationLevelModal, setShowChangeEscalationLevelModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState<any>(null);
  const [resendingInvitation, setResendingInvitation] = useState<string | null>(null);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [userPhones, setUserPhones] = useState<Record<string, string>>({});
  const { users: allUsers } = useUsersStore();
  const { loadUsersPage } = useUsersPage();
  const navigate = useNavigate();
  const { keycloak: { token, tokenParsed } } = useKeycloak();
  const { isFeatureEnabled } = useFeatureFlag();

  if (projectData === 'not-found') {
    return <div>Project not found</div>;
  }

  const projectId = projectData.id || '';
  const hookUsers = useProjectUsers(projectId, archived);

  // Use archived data if provided, otherwise use hook data
  const users = archived && archivedProjectUsers ? archivedProjectUsers.results : hookUsers.users;
  const roles = archived && archivedProjectUsers
    ? [...new Map(archivedProjectUsers.results?.filter(u => u.role).map(u => [u.role.id, u.role])).values()]
    : hookUsers.roles;
  const loading = archived && archivedProjectUsers ? false : hookUsers.loading;
  const error = archived && archivedProjectUsers ? null : hookUsers.error;
  const addUser = archived ? async () => {} : hookUsers.addUser;
  const updateUser = archived ? async () => {} : hookUsers.updateUser;
  const removeUser = archived ? async () => {} : hookUsers.removeUser;
  const loadUsers = archived ? async () => {} : hookUsers.loadUsers;

  // Determine if current user can manage team (add/edit/remove members)
  const currentUserSub = tokenParsed?.sub;
  const currentUserEmail = tokenParsed?.email;
  const isProjectOwner = projectData.owner_id === currentUserSub;
  const allowManageTeam = isProjectOwner || canManage;

  // The access payload describes only the logged-in user, so the RBAC title can
  // label their own row; everyone else falls back to the stored role name.
  const roleLabelFor = (member: ProjectUserWithRole): string => {
    const roleName = roles.find((role: { id: string; name: string }) => role?.id === member.project_role_id)?.name;
    const isCurrentUser =
      (!!currentUserSub && member.user_id === currentUserSub) ||
      (!!currentUserEmail && member.user?.email === currentUserEmail);
    return getDisplayRoleName(roleName, isCurrentUser ? projectAccess?.projectRoleTitle : null);
  };
  
  // Escalation matrix store and page hook
  const { levels } = useEscalationMatrixStore();
  const { loadEscalationMatrixLevels } = useEscalationMatrixPage(projectId);
  const escalationLevels = levels[projectId] || [];
  
  // Debug log
  console.log('ProjectTeam: escalationLevels', escalationLevels);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (isUnmountedRef.current) return;

    const handleClickOutside = () => {
      if (!isUnmountedRef.current) {
      setOpenDropdown(null);
      }
    };

    if (openDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openDropdown]);

  // Cleanup effect to prevent DOM operations after unmount. Reset on setup
  // too, so StrictMode's mount/cleanup/remount simulation doesn't leave this
  // permanently `true` after the initial mount.
  useEffect(() => {
    isUnmountedRef.current = false;
    return () => {
      isUnmountedRef.current = true;
    };
  }, []);

  // Load all users on component mount only if no data exists
  useEffect(() => {
    if (allUsers.length === 0) {
      loadUsersPage(1).catch(error => {
        console.error('Failed to load users page:', error);
      });
    }
  }, []); // Remove dependencies to avoid unnecessary calls

  // Load escalation levels on component mount. Guard against React
  // StrictMode's dev-only double-invoke firing this twice on mount, while
  // still reloading on a genuine projectId change (this component does not
  // remount when switching projects while already on the Team tab).
  const lastLoadedEscalationProjectIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!projectId || lastLoadedEscalationProjectIdRef.current === projectId) return;
    lastLoadedEscalationProjectIdRef.current = projectId;
    loadEscalationMatrixLevels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]); // Remove loadEscalationMatrixLevels from dependencies to avoid infinite requests

  // Fetch user profile phones for team members
  useEffect(() => {
    const fetchUserPhones = async () => {
      if (!token || !users || users.length === 0) return;
      
      const phonesMap: Record<string, string> = {};
      
      // Fetch profiles for users that don't have phone in user object
      const usersNeedingPhone = users.filter(
        (member) => member.user_id && !member.user?.phone
      );
      
      // Fetch in parallel but limit concurrent requests
      const batchSize = 5;
      for (let i = 0; i < usersNeedingPhone.length; i += batchSize) {
        const batch = usersNeedingPhone.slice(i, i + batchSize);
        const promises = batch.map(async (member) => {
          try {
            const profile = await getUserProfile(token, member.user_id);
            if (profile?.phone) {
              phonesMap[member.user_id] = profile.phone;
            }
          } catch (error) {
            // Silently ignore errors for individual profile fetches
            console.debug('Could not fetch profile for user:', member.user_id);
          }
        });
        await Promise.all(promises);
      }
      
      if (Object.keys(phonesMap).length > 0) {
        setUserPhones(prev => ({ ...prev, ...phonesMap }));
      }
    };
    
    fetchUserPhones();
  }, [token, users]);

  const handleAddUser = async (_userId: string, userData: any) => {
    try {
      console.log('ProjectTeam: adding user', userData);
      console.log('ProjectTeam: current users count before:', users?.length);
      // Pass the userData directly since it already contains all required fields
      await addUser('', userData);
      console.log('ProjectTeam: user added successfully');
      
      // Explicitly reload users to ensure fresh data
      await loadUsers();
      console.log('ProjectTeam: current users count after:', users?.length);
      
      // Also trigger project data refresh if callback provided
      if (onProjectUpdate) {
        await onProjectUpdate();
      }
    } catch (error) {
      console.error('ProjectTeam: failed to add user', error);
      throw error;
    }
  };

  const handleUpdateUserRole = async (userId: string, newRoleId: string) => {
    try {
      const user = users.find(u => u.user_id === userId);
      if (user) {
        // Send data according to new API specification
        const updateData = {
          id: user.id, // Required: record ID
          project_role_id: newRoleId, // Only update the role
          // Include existing data to avoid losing it
          first_name: user.user.first_name,
          last_name: user.user.last_name,
          email: user.user.email,
          phone: (user as any).phone,
          escalation_level_id: user.escalation_level_id,
          department: user.department,
          status: user.status,
          skills: user.skills
        };

        await updateUser(userId, updateData);
        toast.success('User role updated successfully');
        
        // Trigger project data refresh
        if (onProjectUpdate) {
          await onProjectUpdate();
        }
      }
    } catch (error) {
      toast.error('Failed to update user role');
    }
  };

  const handleUpdateUserEscalationLevel = async (userId: string, newLevelId: string) => {
    try {
      const user = users.find(u => u.user_id === userId);
      if (user) {
        // Send data according to new API specification
        const updateData = {
          id: user.id, // Required: record ID
          escalation_level_id: newLevelId || '', // Update escalation level (empty string for "Any")
          // Include existing data to avoid losing it
          first_name: user.user.first_name,
          last_name: user.user.last_name,
          email: user.user.email,
          phone: (user as any).phone,
          project_role_id: user.project_role_id,
          department: user.department,
          status: user.status,
          skills: user.skills
        };

        await updateUser(userId, updateData);
        toast.success('Escalation level updated successfully');
        
        // Trigger project data refresh
        if (onProjectUpdate) {
          await onProjectUpdate();
        }
      }
    } catch (error) {
      toast.error('Failed to update escalation level');
    }
  };

  const handleResendInvitation = async (email: string) => {
    if (!token) {
      toast.error('No authentication token');
      return;
    }

    try {
      setResendingInvitation(email);
      await resendInvitation(token, { email });
      toast.success('Invitation resent successfully');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to resend invitation';
      toast.error(errorMessage);
    } finally {
      setResendingInvitation(null);
    }
  };

  const handleEditUser = (member: any) => {
    // Enhance member with phone from profile if available, fallback to project phone
    const enhancedMember = {
      ...member,
      user: {
        ...member.user,
        phone: member.user?.phone || userPhones[member.user_id] || member.phone || ''
      }
    };
    setSelectedMember(enhancedMember);
    setShowEditUserModal(true);
    setOpenDropdown(null);
  };

  const handleUpdateUser = async (userData: any) => {
    try {
      await updateUser(selectedMember.user_id, userData);
      toast.success('User updated successfully');
      
      // Force reload users list to ensure UI is updated
      await loadUsers();

      // A role change can alter the current user's own permissions.
      reloadAccess();
      
      // Also trigger project data refresh
      if (onProjectUpdate) {
        await onProjectUpdate();
      }
    } catch (error) {
      console.error('Failed to update user:', error);
      toast.error('Failed to update user');
    }
  };

  const handleRemoveUser = async (member: any) => {
    if (!window.confirm(`Are you sure you want to remove ${member.user.first_name} ${member.user.last_name} from this project?`)) {
      return;
    }

    try {
      await removeUser(member.user_id);
      toast.success('User removed successfully');
      setOpenDropdown(null);
      reloadAccess();
      
      // Trigger project data refresh
      if (onProjectUpdate) {
        await onProjectUpdate();
      }
    } catch (error) {
      toast.error('Failed to remove user');
    }
  };

  if (loading) {
    return <LoadingSpinner text="Loading team members..." />;
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500">
          <p>Error: {error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="mt-2 px-4 py-2 bg-[#171717] text-white rounded hover:bg-[#333] transition-colors"
          >
            Reload Page
          </button>
        </div>
      </div>
    );
  }

  // Safety check for users array
  if (!Array.isArray(users)) {
    console.error('ProjectTeam: users is not an array', users);
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500">
          <p>Invalid data format. Please refresh the page.</p>
          <button 
            onClick={() => window.location.reload()} 
            className="mt-2 px-4 py-2 bg-[#171717] text-white rounded hover:bg-[#333] transition-colors"
          >
            Reload Page
          </button>
        </div>
      </div>
    );
  }

  return (
    <ProjectTeamErrorBoundary>
      {projectAccessError && (
        <div className="mb-4 border border-[#eaeaea] bg-[#fafafa] rounded px-4 py-3 text-sm text-[#b45309]" role="status">
          Couldn't load your permissions, so team management is unavailable. Reload to try again.
        </div>
      )}
      <div className="flex justify-between items-center mb-4">
        <div>
          <h3 className="text-lg font-medium text-[#171717]">Team Members</h3>
          <p className="text-sm text-[#888]">{users.length} member{users.length !== 1 ? 's' : ''}</p>
        </div>
        {allowManageTeam && (
          <button
            onClick={() => setIsShowAddModal(true)}
            className="cursor-pointer bg-[#171717] hover:bg-[#333] border border-[#171717] text-white font-medium py-2 px-4 rounded-lg transition-colors"
          >
            + Add Team Member
          </button>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-[#eaeaea]">
          <thead className="bg-[#fafafa]">
            <tr>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-[#888] uppercase tracking-wider">
                Member
              </th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-[#888] uppercase tracking-wider">
                Contact
              </th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-[#888] uppercase tracking-wider">
                Role
              </th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-[#888] uppercase tracking-wider">
                Escalation Level
              </th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-[#888] uppercase tracking-wider">
                Join Date
              </th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-[#888] uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-[#eaeaea]">
            {(() => {
              const filteredUsers = (users || []).filter((member) => {
                // Filter out users with Developer roles in production
                if (!isFeatureEnabled('SHOW_DEVELOPER_ROLES')) {
                  const memberRole = roles.find(role => role?.id === member.project_role_id);
                  if (memberRole?.name?.toLowerCase().includes('developer')) {
                    return false;
                  }
                }
                return true;
              });
              
              return filteredUsers.map((member, index) => {
                const isLastRow = index >= filteredUsers.length - 2; // Last 2 rows show dropdown above
              try {
                // Safety check for member data
                if (!member || !member.user) {
                  console.warn('ProjectTeam: invalid member data', member);
                  return null;
                }



                return (
                  <tr 
                    key={member.id || member.user_id} 
                    className="hover:bg-[#fafafa] cursor-pointer"
                    onClick={() => navigate(`/users/${member.user.id}`)}
                  >
                    {/* MEMBER: Avatar + Name + Verified/Pending */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <Avatar
                          firstName={member.user.first_name}
                          lastName={member.user.last_name}
                          email={member.user.email}
                          size={40}
                        />
                        <div className="flex flex-col">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-[#171717]">
                              {member.user.first_name || ''} {member.user.last_name || ''}
                            </span>
                            {/* Verified/Pending status */}
                            {member.user.id === projectData.owner_id ? (
                              <svg className="w-4 h-4 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            ) : (
                              <>
                                {member.user.email && !member.user.is_email_verified && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                    Pending
                                  </span>
                                )}
                                {member.user.email && member.user.is_email_verified && (
                                  <svg className="w-4 h-4 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                )}
                              </>
                            )}
                          </div>
                          {member.position && (
                            <span className="text-xs text-[#888]">{member.position}</span>
                          )}
                        </div>
                      </div>
                    </td>
                    {/* CONTACT: Email + Phone */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-1.5 text-sm text-[#171717]">
                          <svg className="w-4 h-4 text-[#aaa] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                          <span>{member.user.email || 'N/A'}</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-sm text-[#888]">
                          <svg className="w-4 h-4 text-[#aaa] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                          </svg>
                          <span>{formatPhoneForDisplay(member.user?.phone || userPhones[member.user_id] || (member as any).phone) || 'N/A'}</span>
                        </div>
                      </div>
                    </td>
                    {/* ROLE */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {allowManageTeam ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedMember(member);
                              setShowChangeRoleModal(true);
                            }}
                            className="text-sm text-[#171717] border border-[#eaeaea] rounded px-3 py-2 hover:bg-[#fafafa] focus:outline-none focus:border-[#888] focus:ring-0 transition-colors"
                          >
                            {roleLabelFor(member)}
                          </button>
                        ) : (
                          <span className="text-sm text-[#171717]">
                            {roleLabelFor(member)}
                          </span>
                        )}
                        {member.user.id === projectData.owner_id && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-[#f5f5f5] text-[#171717]">
                            Owner
                          </span>
                        )}
                      </div>
                    </td>
                    {/* ESCALATION LEVEL */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      {allowManageTeam ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedMember(member);
                            setShowChangeEscalationLevelModal(true);
                          }}
                          className="text-sm text-[#171717] border border-[#eaeaea] rounded px-3 py-2 hover:bg-[#fafafa] focus:outline-none focus:border-[#888] focus:ring-0 transition-colors"
                        >
                          {member.escalation_level?.name || 'Any'}
                        </button>
                      ) : (
                        <span className="text-sm text-[#171717]">
                          {member.escalation_level?.name || 'Any'}
                        </span>
                      )}
                    </td>
                    {/* JOIN DATE */}
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-[#888]">
                      {member.joined_at ? new Date(member.joined_at).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm">
                      {allowManageTeam && (
                        <div className="flex items-center justify-end">
                          <div className="relative">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenDropdown(openDropdown === member.user.email ? null : member.user.email);
                              }}
                              className="p-1 rounded-md hover:bg-[#fafafa] focus:outline-none"
                            >
                              <svg className="w-4 h-4 text-[#888]" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                              </svg>
                            </button>
                            {openDropdown === member.user.email && (
                              <div className={`absolute right-0 z-50 w-48 bg-white rounded-md shadow-[0_4px_12px_rgba(0,0,0,0.08)] border border-[#eaeaea] ${isLastRow ? 'bottom-full mb-1' : 'mt-1'}`}>
                                <div className="py-1">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEditUser(member);
                                    }}
                                    className="w-full text-left px-4 py-2 text-sm text-[#444] hover:bg-[#fafafa] flex items-center gap-2"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                    </svg>
                                    Edit User
                                  </button>
                                  {!member.user.is_email_verified && member.user.id !== projectData.owner_id && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleResendInvitation(member.user.email);
                                        setOpenDropdown(null);
                                      }}
                                      disabled={resendingInvitation === member.user.email}
                                      className="w-full text-left px-4 py-2 text-sm text-[#444] hover:bg-[#fafafa] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                      </svg>
                                      {resendingInvitation === member.user.email ? 'Sending...' : 'Resend Invitation'}
                                    </button>
                                  )}
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRemoveUser(member);
                                    }}
                                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                    Remove User
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              } catch (renderError) {
                console.error('ProjectTeam: error rendering member', member, renderError);
                return null;
              }
            }).filter(Boolean);
            })()}
          </tbody>
        </table>
      </div>

      {users.length === 0 && (
        <div className="text-center py-8">
          <div className="text-[#888] mb-4">No team members yet</div>
          {allowManageTeam && (
            <button
              onClick={() => setIsShowAddModal(true)}
              className="cursor-pointer w-full p-2 text-left hover:bg-[#fafafa] text-[#888] text-sm border border-[#eaeaea] rounded"
            >
              + Add Team Member
            </button>
          )}
        </div>
      )}

      <AddUserModal
        isOpen={isShowAddModal}
        onClose={() => setIsShowAddModal(false)}
        onAddUser={handleAddUser}
        availableUsers={allUsers}
        projectUsers={users.map(u => u.user)}
        roles={roles}
        escalationLevels={escalationLevels}
        projectId={projectId}
        onEscalationLevelsUpdate={loadEscalationMatrixLevels}
      />

      {selectedMember && (
        <ChangeRoleModal
          isOpen={showChangeRoleModal}
          onClose={() => {
            setShowChangeRoleModal(false);
            setSelectedMember(null);
          }}
          onChangeRole={(newRoleId: string) => handleUpdateUserRole(selectedMember.user_id, newRoleId)}
          currentRoleId={selectedMember.project_role_id || ''}
          roles={roles}
        />
      )}

      {selectedMember && (
        <ChangeEscalationLevelModal
          isOpen={showChangeEscalationLevelModal}
          onClose={() => {
            setShowChangeEscalationLevelModal(false);
            setSelectedMember(null);
          }}
          onChangeEscalationLevel={(newLevelId: string) => handleUpdateUserEscalationLevel(selectedMember.user_id, newLevelId)}
          currentLevelId={selectedMember.escalation_level_id || ''}
          levels={escalationLevels}
        />
      )}

      {selectedMember && (
        <EditUserModal
          isOpen={showEditUserModal}
          onClose={() => {
            setShowEditUserModal(false);
            setSelectedMember(null);
          }}
          onUpdateUser={handleUpdateUser}
          user={selectedMember}
          roles={roles}
          escalationLevels={escalationLevels}
        />
      )}
    </ProjectTeamErrorBoundary>
  );
}; 